import React, { useEffect, useMemo, useState } from "react";
import { FaRegUserCircle } from "react-icons/fa";
import { NavLink } from "react-router-dom";
import axios from "axios";
import apiKey from "../../../config/apiKey";
import { useTranslation } from "react-i18next";

const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-100 rounded ${className}`} />
);
const RelativeTime = ({ date }) => {
  const { t } = useTranslation();
  const d = new Date(date);
  const diff = (Date.now() - d.getTime()) / 1000;
  const mins = Math.floor(diff / 60);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  let label = d.toLocaleString();
  if (mins < 1) label = t("adminDash.notification.justNow");
  else if (mins < 60) label = t("adminDash.notification.minsAgo", { count: mins });
  else if (hours < 24) label = t("adminDash.notification.hoursAgo", { count: hours });
  else if (days < 7) label = t("adminDash.notification.daysAgo", { count: days });
  return <span title={d.toLocaleString()} className="text-[12px] text-gray-500">{label}</span>;
};

export default function UnreadNotification() {
  const { t } = useTranslation();
  const admin = useMemo(() => JSON.parse(localStorage.getItem("edo-admin")), []);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState("");

  const fetchNotifications = async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.get(`${apiKey}/api/edolutions/admin/notifications/unread`, {
        headers: { Authorization: `Bearer ${admin?.token}` },
      });
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Error fetching notifications:", e);
      setError(t("adminDash.notification.loadUnreadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotifications(); }, [admin?.token]);

  return (
    <div className="flex items-center xl:p-5 sm:p-0 justify-center">
      <div className="w-full border rounded-3xl shadow-sm bg-[#F9FAFB]">
        {/* Tabs header */}
        <div className="px-6 pt-6 pb-2 bg-gradient-to-r from-[#F8FFFE] to-[#F5FAFF] rounded-t-3xl">
          <h1 className="text-2xl font-bold text-gray-800">{t("adminDash.notifications")}</h1>
          <p className="text-xs text-gray-500 mt-1">{t("adminDash.notification.adminFeed")}</p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <NavLink to=".././all-notification" className={({ isActive }) =>
              `inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                isActive ? "bg-[#EE5F2B] text-white" : "bg-white text-gray-700 border border-gray-200 hover:bg-orange-400/10 hover:text-orange-400"
              }`
            }>
              {t("adminDash.studentNoti.all")}
            </NavLink>
            {/* <NavLink to=".././newNotification" className={({ isActive }) =>
              `inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                isActive ? "bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] text-white" : "bg-white text-gray-700 border border-gray-200 hover:bg-[#E6FFFB] hover:text-[#169d8f]"
              }`
            }>
              New
            </NavLink> */}
            <NavLink to=".././unread-notification" className={({ isActive }) =>
              `inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                isActive ? "bg-[#EE5F2B] text-white" : "bg-white text-gray-700 border border-gray-200 hover:bg-orange-400/10 hover:text-orange-400"
              }`
            }>
              {t("adminDash.studentNoti.unread")}
            </NavLink>
          </div>
        </div>

        {/* sticky tools row */}
        <div className="sticky top-0 z-10 bg-[#F9FAFB]/90 backdrop-blur px-6 py-3 border-b flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {t("adminDash.studentNoti.unread")}: <span className="font-semibold">{notifications.length}</span>
          </p>
          <div className="flex items-center gap-2">
            {/* Hook up when you add endpoint */}
            {/* <button className="text-xs px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50">Mark all as read</button> */}
            <NavLink to=".././all-notification" className="text-xs px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-50">
              {t("adminDash.dashboardHome.viewAll")}
            </NavLink>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-10">
          {loading && (
            <div className="space-y-2">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14" />)}
            </div>
          )}

          {!loading && error && (
            <div className="py-8 text-center text-gray-600">{error}</div>
          )}

          {!loading && !error && notifications.length === 0 && (
            <div className="py-8 text-center text-gray-500">{t("adminDash.notification.noUnreadNotifications")}</div>
          )}

          {!loading && !error && notifications.length > 0 && (
            <ul className="divide-y rounded-xl border bg-white">
              {notifications.map((n) => (
                <li key={n.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <span className="mt-2 h-2.5 w-2.5 rounded-full bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] shrink-0" />
                    <FaRegUserCircle className="h-7 w-7 text-gray-500 shrink-0" />
                    <div className="flex-1">
                      <p className="text-[14px] font-semibold text-gray-800">{n.message}</p>
                      <RelativeTime date={n.created_at} />
                    </div>
                    {/* Per-item action can go here */}
                    {/* <button className="text-xs px-2 py-1 rounded border hover:bg-gray-50">Mark read</button> */}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
